// ----------------------------------------------------------------------------
// Roster. One team at a time (the header's team switcher): month or list of
// services on the left, the selected service's panel on the right.
// Past services are a record and can't be changed.
// ----------------------------------------------------------------------------

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { Toast, ConfirmDialog } from '@app/components/Notifications';
import { Banner, Button, EmptyState, ErrorState, Meter, PageHeader, Segmented, Select } from '@app/components/ui';
import { useMediaQuery, BP } from '@app/hooks/useMediaQuery';
import { isPastDate, plural } from '@app/lib/format';
import { redesignFlags } from '@app/featureFlags';
import { plansApi, templatesApi, songsApi, availabilityApi } from '@app/api/organization';
import type { PlanItem, SetlistTemplate, AvailabilityStatusValue, ServicePlan } from '@app/types';
import { RosterHeader } from '../components/roster/RosterHeader';
import { PublishModal as RosterPublishModal } from '../components/roster/PublishModal';
import { EditServiceModal } from '../components/roster/EditServiceModal';
import { RescheduleServiceDialog } from '../components/roster/RescheduleServiceDialog';
import { SetlistBuilder, type PendingYouTubeSong } from '../components/roster/SetlistBuilder';
import { RosterCalendar, RosterList, fillState } from '../components/roster/RosterViews';
import { TeamSlots } from '../components/roster/TeamSlots';
import { NewPlanWizard } from '../components/roster/NewPlanWizard';
import { useRosterData } from '../hooks/useRosterData';
import { useRosterActions } from '../hooks/useRosterActions';
import { usePlanDraft } from '../hooks/usePlanDraft';
import { SETLIST_MODE_LABEL, setlistView } from '../utils/setlistVisibility';

export const Roster: React.FC = () => {
  const [params] = useSearchParams();
  // Create → New service plan (and "+ New service plan" here) open the wizard.
  if (params.get('plan') === 'new') return <NewPlanWizard />;
  return <RosterPage />;
};

const RosterPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedGroupId, groups, selectedGroup } = useGroup();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const churchId = user?.churchId;
  const wide = useMediaQuery(BP.rosterCalendar);

  const { allPlans, setAllPlans, availableMembers, loading, loadError, reload, orphanedPlanCount, setOrphanedPlanCount } = useRosterData(churchId, selectedGroupId);

  // Only this team's services (orphans aside, which get their own banner).
  const plans = useMemo(
    () => allPlans.filter((p) => !selectedGroupId || p.groupId === selectedGroupId).sort((a, b) => +new Date(a.date) - +new Date(b.date)),
    [allPlans, selectedGroupId],
  );

  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [tab, setTab] = useState<'team' | 'setlist'>('team');
  const [building, setBuilding] = useState<string | null>(null);
  const {
    selectedPlan,
    setSelectedPlan,
    localTeam,
    setLocalTeam,
    localNotes,
    setLocalNotes,
    setLocalItems,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    itemsForSave,
    clearDraft,
    discardDraft,
  } = usePlanDraft(selectedPlanId, allPlans);

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const flash = (m: string, t: 'success' | 'error' | 'info' = 'success') => {
    setToastType(t);
    setToastMessage(m);
  };
  const [isResendingLyrics, setIsResendingLyrics] = useState(false);
  const [isPublishingSet, setIsPublishingSet] = useState(false);

  // Availability on the selected service's date (userId -> status) and who is
  // on long-term leave; both disable people in the add-person picker.
  const [availabilityByUser, setAvailabilityByUser] = useState<Record<string, AvailabilityStatusValue>>({});
  const [onLeaveUserIds, setOnLeaveUserIds] = useState<Set<string>>(new Set());

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isEditServiceOpen, setIsEditServiceOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  // Bumped on discard so the setlist builder drops its own copy of the items.
  const [builderKey, setBuilderKey] = useState(0);

  const [templates, setTemplates] = useState<SetlistTemplate[]>([]);
  const [pendingYouTubeSongs, setPendingYouTubeSongs] = useState<PendingYouTubeSong[]>([]);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'default' | 'danger' | 'success';
    confirmText?: string;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const { handlePublish: publishRoster, handleDelete: deleteRoster, isPublishing } = useRosterActions(
    churchId,
    user,
    selectedPlan,
    setSelectedPlan,
    setAllPlans,
    setHasUnsavedChanges,
    setToastMessage,
    setToastType,
  );

  // Deep links: ?plan=<id> selects a service; &build=1 opens setlist building;
  // &tab=setlist opens the published setlist.
  // The tab a deep link asked for, applied when its service becomes selected
  // (the effect below resets the tab on every selection change).
  const pendingTab = useRef<{ planId: string; tab: 'team' | 'setlist' } | null>(null);
  useEffect(() => {
    const id = params.get('plan');
    if (!id || id === 'new' || !plans.length) return;
    const p = plans.find((x) => x.id === id);
    if (p) {
      setSelectedPlanId(p.id);
      const d = new Date(p.date);
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      if (params.get('build') === '1') setBuilding(p.id);
      if (params.get('tab') === 'setlist') pendingTab.current = { planId: p.id, tab: 'setlist' };
    }
    // Copy, don't mutate: the default-selection effect below runs in the same
    // commit and must still see the pending ?plan= so it doesn't override it.
    const next = new URLSearchParams(params);
    next.delete('plan');
    next.delete('build');
    next.delete('tab');
    setParams(next, { replace: true });
  }, [params, setParams, plans]);

  // Default selection: the next upcoming service, else the latest one.
  // Steps aside while a ?plan= deep link is pending.
  useEffect(() => {
    const deep = params.get('plan');
    if (deep && deep !== 'new' && plans.some((p) => p.id === deep)) return;
    if (selectedPlanId && plans.some((p) => p.id === selectedPlanId)) return;
    const next = plans.find((p) => !isPastDate(p.date)) ?? plans[plans.length - 1];
    setSelectedPlanId(next?.id ?? null);
  }, [plans, selectedPlanId, params]);

  useEffect(() => {
    const pending = pendingTab.current;
    if (pending && pending.planId === selectedPlanId) {
      setTab(pending.tab);
      pendingTab.current = null;
    } else {
      setTab('team');
    }
  }, [selectedPlanId]);

  useEffect(() => {
    const groupId = selectedPlan?.groupId ?? selectedGroupId ?? undefined;
    const dateStr = selectedPlan?.date;
    if (!churchId || !groupId || !dateStr) {
      setAvailabilityByUser({});
      setOnLeaveUserIds(new Set());
      return undefined;
    }
    const d = new Date(dateStr);
    let cancelled = false;
    availabilityApi
      .getTeam(churchId, groupId, d.getFullYear(), d.getMonth() + 1)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, AvailabilityStatusValue> = {};
        const leave = new Set<string>();
        for (const row of rows) {
          if (row.onLeave) leave.add(row.userId);
          const entry = row.days.find((day) => day.day === d.getDate());
          if (entry && entry.status !== 'Available') map[row.userId] = entry.status;
        }
        setAvailabilityByUser(map);
        setOnLeaveUserIds(leave);
      })
      .catch(() => {
        if (!cancelled) {
          setAvailabilityByUser({});
          setOnLeaveUserIds(new Set());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [churchId, selectedGroupId, selectedPlan?.id, selectedPlan?.date, selectedPlan?.groupId]);

  useEffect(() => {
    if (!churchId) return;
    templatesApi
      .getByChurch(churchId, selectedGroupId ?? undefined)
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [churchId, selectedGroupId]);

  const handleSaveTemplate = async (name: string, items: PlanItem[]) => {
    if (!churchId) return;
    try {
      const created = await templatesApi.create(churchId, { name, items, groupId: selectedGroupId ?? undefined });
      setTemplates((prev) => [...prev, created]);
      flash('Template saved');
    } catch {
      flash('Failed to save template', 'error');
    }
  };

  const handleResendLyrics = async () => {
    if (!churchId || !selectedPlan || isResendingLyrics) return;
    setIsResendingLyrics(true);
    try {
      const result = await plansApi.replayNotification(churchId, selectedPlan.id, 'plan.setlist_lyrics');
      if (result.sent) {
        flash(`Lyrics email sent to ${result.recipientCount ?? 1} recipient${(result.recipientCount ?? 1) === 1 ? '' : 's'}`);
      } else {
        flash(result.reason ?? 'Nothing to send', 'error');
      }
    } catch (err: unknown) {
      flash((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to resend the lyrics email', 'error');
    } finally {
      setIsResendingLyrics(false);
    }
  };

  const handleEditService = async (data: { title: string; date: string; time: string; notes: string }) => {
    if (!churchId || !selectedPlan) return;
    try {
      const [hours, minutes] = data.time.split(':');
      const dt = new Date(data.date + 'T12:00:00');
      dt.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      const updated = await plansApi.update(churchId, selectedPlan.id, { title: data.title, date: dt.toISOString(), notes: data.notes || '' });
      setAllPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setLocalNotes(data.notes || '');
      setMonth(new Date(dt.getFullYear(), dt.getMonth(), 1));
      setIsEditServiceOpen(false);
      flash('Service updated');
    } catch (err: unknown) {
      // Surface the backend's own message; if it is the past-plan
      // immutability rule, open Reschedule as the way forward.
      const e = err as { response?: { data?: { error?: string }; status?: number }; message?: string };
      const backendMessage = e?.response?.data?.error;
      flash(backendMessage || e?.message || 'Failed to update service', 'error');
      if (e?.response?.status === 400 && typeof backendMessage === 'string' && /reschedule|already happened/i.test(backendMessage)) {
        setIsEditServiceOpen(false);
        setIsRescheduleOpen(true);
      }
    }
  };

  const handlePublish = async () => {
    if (!selectedPlan || !churchId || !user) return;
    const planDate = new Date(selectedPlan.date);
    const h = planDate.getHours();
    const timeStr = `${h % 12 || 12}:${planDate.getMinutes().toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;

    // Save YouTube-sourced songs to library on publish
    if (pendingYouTubeSongs.length > 0) {
      try {
        const result = await songsApi.batchCreate(
          churchId,
          pendingYouTubeSongs.map((yt) => ({
            churchId,
            groupId: selectedGroupId ?? undefined,
            title: yt.title,
            author: yt.channelTitle,
            youtubeUrl: yt.youtubeUrl,
            tags: ['youtube'],
            createdBy: user.id,
          })),
        );
        if (result.created.length > 0) {
          flash(
            `${result.created.length} song${result.created.length > 1 ? 's' : ''} saved to your library${result.duplicates.length > 0 ? ` (${result.duplicates.length} already existed)` : ''}`,
            'info',
          );
        }
        setPendingYouTubeSongs([]);
      } catch {
        // Non-blocking — publish continues even if library save fails
      }
    }

    publishRoster(planDate, selectedPlan.title, localNotes, localTeam, setIsPublishModalOpen, timeStr, selectedPlan.duration, selectedPlan.serviceType, itemsForSave(selectedPlan));
  };

  const handleSaveDraft = async () => {
    if (!churchId || !selectedPlan) return;
    try {
      const updated = await plansApi.updateRoster(churchId, selectedPlan.id, {
        date: selectedPlan.date,
        title: selectedPlan.title,
        notes: localNotes,
        items: itemsForSave(selectedPlan),
        team: localTeam,
        isPublished: selectedPlan.isPublished,
      });
      setAllPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      clearDraft(selectedPlan.id);
      flash('Draft saved');
    } catch {
      flash('Failed to save draft', 'error');
    }
  };

  // Publish the admin-built setlist (sets isSetlistPublished) with its items.
  const handlePublishSetlist = async () => {
    if (!churchId || !selectedPlan) return;
    const items = itemsForSave(selectedPlan);
    if (!items?.length) {
      flash('Add at least one song first', 'error');
      return;
    }
    setIsPublishingSet(true);
    try {
      const updated = await plansApi.update(churchId, selectedPlan.id, { items, isSetlistPublished: true });
      setAllPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setBuilding(null);
      setTab('setlist');
      flash('Setlist published. The team can see it now.');
    } catch {
      flash('Failed to publish the setlist', 'error');
    } finally {
      setIsPublishingSet(false);
    }
  };

  const handleModeChange = async (val: string) => {
    if (!churchId || !selectedPlan) return;
    try {
      const updated = await plansApi.update(churchId, selectedPlan.id, { setlistModeOverride: val || undefined });
      setAllPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setBuilding(null);
      setTab('team');
      flash('Setlist mode updated');
    } catch {
      flash('Failed to update setlist mode', 'error');
    }
  };

  // --- Team edits (local until published or saved as a draft) ---
  const commitAssignMember = (userId: string, name: string, roleName: string) => {
    setLocalTeam((prev) => [...prev, { id: `${userId}-${Date.now()}`, userId, name, role: roleName, status: 0 }]);
    setHasUnsavedChanges(true);
    flash(`${name} assigned to ${roleName}`);
  };

  const handleAssignPlaceholder = (name: string, roleName: string) => {
    const placeholderGuid = crypto.randomUUID();
    setLocalTeam((prev) => [...prev, { id: placeholderGuid, userId: placeholderGuid, name, role: roleName, status: 0, isPlaceholder: true }]);
    setHasUnsavedChanges(true);
    flash(`Placeholder "${name}" added to ${roleName}`);
  };

  const handleUpdateStatus = async (userId: string, status: number) => {
    if (!churchId || !selectedPlan?.id) return;
    setLocalTeam((prev) => prev.map((m) => (m.userId === userId ? { ...m, status } : m)));
    setHasUnsavedChanges(true);
    // Only call the status API if the member already exists on the server.
    const existsOnServer = selectedPlan.team?.some((m) => m.userId === userId);
    if (existsOnServer) {
      try {
        await plansApi.updateTeamMemberStatus(churchId, selectedPlan.id, userId, status);
        setAllPlans((prev) => prev.map((p) => (p.id === selectedPlan.id ? { ...p, team: p.team.map((m) => (m.userId === userId ? { ...m, status } : m)) } : p)));
      } catch {
        // Will persist when the roster is saved
      }
    } else {
      // New member not yet on the server — save the draft to persist the status.
      try {
        const updatedTeam = localTeam.map((m) => (m.userId === userId ? { ...m, status } : m));
        const updated = await plansApi.updateRoster(churchId, selectedPlan.id, {
          date: selectedPlan.date,
          title: selectedPlan.title,
          notes: localNotes,
          team: updatedTeam,
          isPublished: selectedPlan.isPublished,
        });
        setAllPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setHasUnsavedChanges(false);
        clearDraft(selectedPlan.id);
      } catch {
        // Will persist when the roster is manually saved
      }
    }
  };

  const handleMigrateOrphans = async () => {
    if (!churchId || groups.length === 0) return;
    setIsMigrating(true);
    try {
      const result = await plansApi.migrateOrphanedPlans(churchId, groups[0].id);
      setOrphanedPlanCount(0);
      flash(`Moved ${result.migrated} roster(s) to ${result.targetGroupName}`);
      reload();
    } catch {
      flash('Failed to migrate rosters', 'error');
    } finally {
      setIsMigrating(false);
    }
  };

  const selectPlan = (p: ServicePlan) => {
    setSelectedPlanId(p.id);
    setBuilding(null);
  };

  const upcomingCount = plans.filter((p) => !isPastDate(p.date)).length;
  const teamName = selectedGroup?.name ?? 'this team';
  const view_ = selectedPlan ? setlistView(selectedPlan, selectedGroup) : null;
  const readOnly = !!view_?.readOnly;
  const isBuilding = !!selectedPlan && building === selectedPlan.id && !!view_?.canBuild;
  const showSetlist = !!view_ && (isBuilding || (view_.showToggle && tab === 'setlist'));
  const fill = selectedPlan ? fillState({ ...selectedPlan, team: localTeam }) : null;
  const teamDefault = (selectedGroup?.setlistMode as keyof typeof SETLIST_MODE_LABEL) || 'leader_builds';

  return (
    <div>
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage('')} type={toastType} />}
      <PageHeader
        eyebrow={`${plural(upcomingCount, 'upcoming service')} · ${teamName}`}
        title="Roster"
        actions={
          <>
            {wide && (
              <Segmented
                label="Roster view"
                value={view}
                onChange={setView}
                options={[
                  { value: 'calendar', label: 'Month' },
                  { value: 'list', label: 'List' },
                ]}
              />
            )}
            <Button variant="dark" onClick={() => navigate('/admin/roster?plan=new')}>
              + New service plan
            </Button>
          </>
        }
      />

      {orphanedPlanCount > 0 && groups.length > 0 && (
        <Banner tone="amb" className="flex flex-wrap items-center gap-3 mb-3.5">
          <AlertTriangle size={16} aria-hidden="true" className="shrink-0" />
          <span className="flex-1 min-w-[220px] text-t1">
            <span className="font-semibold">{plural(orphanedPlanCount, 'roster')} {orphanedPlanCount > 1 ? 'were' : 'was'} created before teams.</span> Move{' '}
            {orphanedPlanCount > 1 ? 'them' : 'it'} to {groups[0].name} so {orphanedPlanCount > 1 ? 'they show' : 'it shows'} under a team.
          </span>
          <Button size="sm" onClick={handleMigrateOrphans} loading={isMigrating} className="!bg-amb">
            Move to {groups[0].name}
          </Button>
        </Banner>
      )}

      {loadError ? (
        <ErrorState title="Couldn't load the roster" body="Check your connection and try again." onRetry={reload} retrying={loading} />
      ) : loading ? (
        <div className="flex flex-wrap gap-3.5" aria-busy="true">
          <span className="wp-skeleton flex-[2_1_560px] h-[520px] rounded-[24px]" />
          <span className="wp-skeleton flex-[1_1_340px] h-[420px] rounded-[24px]" />
        </div>
      ) : plans.length === 0 ? (
        <EmptyState
          title={`${selectedGroup?.name ?? 'This team'} has no services yet`}
          body="Plan the first one and build its team and setlist."
          action={<Button onClick={() => navigate('/admin/roster?plan=new')}>Plan a service</Button>}
        />
      ) : (
        <div className="flex flex-wrap gap-3.5 items-start">
          <div className="flex-[2_1_560px] min-w-0">
            {wide && view === 'calendar' ? (
              <RosterCalendar month={month} onMonth={setMonth} plans={plans} selectedId={selectedPlanId} onSelect={selectPlan} />
            ) : (
              <RosterList plans={plans} selectedId={selectedPlanId} onSelect={selectPlan} />
            )}
          </div>

          {selectedPlan && view_ && fill && (
            <section
              aria-label="Selected service"
              className="flex-[1_1_340px] min-w-0 rounded-[24px] border border-line bg-card p-[22px] min-[1000px]:sticky min-[1000px]:top-[84px]"
            >
              <RosterHeader
                selectedPlan={selectedPlan}
                selectedDate={new Date(selectedPlan.date)}
                onPublishClick={() => setIsPublishModalOpen(true)}
                onEditDetails={() => setIsEditServiceOpen(true)}
                onDeleteService={() => deleteRoster(setConfirmDialog, confirmDialog, setLocalTeam, setLocalNotes)}
                onReschedule={() => setIsRescheduleOpen(true)}
                onResendLyrics={handleResendLyrics}
                isResendingLyrics={isResendingLyrics}
                onSaveDraft={handleSaveDraft}
                onDiscard={() => setDiscardOpen(true)}
                isPublishing={isPublishing}
                hasUnsavedChanges={hasUnsavedChanges}
                readOnly={readOnly}
              />

              <div className="flex items-center gap-2.5 mt-3.5 mb-4">
                <Meter value={fill.confirmed} limit={Math.max(fill.total, 1)} className="flex-1" label="Confirmed" />
                <span className="font-mono text-[13px] font-bold">
                  {fill.confirmed}/{fill.total}
                </span>
                <span className="text-[12px] text-t4">confirmed</span>
              </div>

              {view_.showToggle && !isBuilding && (
                <Segmented
                  label="Service details"
                  block
                  className="mb-2"
                  value={tab}
                  onChange={setTab}
                  options={[
                    { value: 'team', label: 'Team' },
                    { value: 'setlist', label: 'Setlist' },
                  ]}
                />
              )}

              {isBuilding && (
                <div className="flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-[12px] bg-amb-bg mb-2">
                  <span className="text-[13px] font-semibold text-amb">Building setlist · not published</span>
                  <button type="button" onClick={() => setBuilding(null)} className="text-[12.5px] text-t2 hover:text-t1">
                    Back to team
                  </button>
                </div>
              )}

              {showSetlist ? (
                <div className="flex flex-col gap-3.5">
                  {readOnly ? (
                    selectedPlan.items.length === 0 ? (
                      <p className="text-[13.5px] text-t4 pt-4 pb-2 border-t border-line">No songs.</p>
                    ) : (
                      <ol>
                        {selectedPlan.items.map((it, i) => (
                          <li key={it.id} className="flex items-center gap-2.5 py-2.5 border-t border-line">
                            <span className="font-mono text-[12px] text-t4 w-4">{i + 1}</span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-[13.5px] font-semibold truncate">{it.title}</span>
                              {it.section && <span className="block text-[12px] text-t4">{it.section}</span>}
                            </span>
                            {it.key && <span className="font-mono text-[12.5px] font-bold text-acc bg-acc-bg rounded-[8px] px-2 py-1">{it.key}</span>}
                          </li>
                        ))}
                      </ol>
                    )
                  ) : (
                    churchId && (
                      <SetlistBuilder
                        key={`${selectedPlan.id}-${builderKey}`}
                        churchId={churchId}
                        plan={selectedPlan}
                        groupId={selectedGroupId}
                        onItemsChange={(items) => {
                          setLocalItems(items);
                          const saved = selectedPlan.items || [];
                          const changed =
                            items.length !== saved.length ||
                            items.some((item, i) => item.id !== saved[i]?.id || item.title !== saved[i]?.title || item.key !== saved[i]?.key || item.section !== saved[i]?.section);
                          if (changed) setHasUnsavedChanges(true);
                        }}
                        onSaveTemplate={handleSaveTemplate}
                        templates={templates}
                        onLoadTemplate={() => setHasUnsavedChanges(true)}
                        onPendingYouTubeSongs={setPendingYouTubeSongs}
                      />
                    )
                  )}
                  {isBuilding && (
                    <Button block onClick={handlePublishSetlist} loading={isPublishingSet}>
                      Publish setlist
                    </Button>
                  )}
                  {!isBuilding && view_.canSendLyrics && (
                    <Button variant="ghost" block onClick={handleResendLyrics} loading={isResendingLyrics}>
                      Send lyrics email
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <TeamSlots
                    team={localTeam}
                    readOnly={readOnly}
                    members={availableMembers}
                    availabilityByUser={availabilityByUser}
                    onLeaveUserIds={onLeaveUserIds}
                    onSwitchRole={(userId, role) => {
                      setLocalTeam((prev) => prev.map((m) => (m.userId === userId ? { ...m, role } : m)));
                      setHasUnsavedChanges(true);
                    }}
                    onRemove={(userId) => {
                      setLocalTeam((prev) => prev.filter((t) => t.userId !== userId));
                      setHasUnsavedChanges(true);
                    }}
                    onUpdateStatus={handleUpdateStatus}
                    onAdd={(userId, role) => {
                      const m = availableMembers.find((x) => x.id === userId);
                      if (m) commitAssignMember(userId, m.username, role);
                    }}
                    onAddPlaceholder={handleAssignPlaceholder}
                  />
                  {!readOnly && redesignFlags.rosterRemindPending && (
                    <Button variant="ghost" block className="mt-4">
                      Remind pending
                    </Button>
                  )}

                  {!readOnly && selectedGroupId && (
                    <div className="mt-4 pt-3.5 border-t border-line">
                      <div className="wp-eyebrow mb-2" id="mode-label">
                        Setlist mode for this service
                      </div>
                      <Select
                        aria-labelledby="mode-label"
                        value={selectedPlan.setlistModeOverride || ''}
                        onChange={handleModeChange}
                        options={[
                          { value: '', label: 'Use team default', sub: SETLIST_MODE_LABEL[teamDefault] },
                          { value: 'leader_builds', label: 'Worship leader builds', sub: 'The leader adds songs and publishes' },
                          { value: 'admin_predefined', label: 'Admin predefined', sub: 'You choose the songs and publish' },
                        ]}
                      />
                      <p className="text-[12.5px] text-t3 mt-2 leading-normal">
                        {view_.mode === 'leader_builds'
                          ? 'The worship leader builds and publishes the setlist for this service.'
                          : selectedPlan.isSetlistPublished
                            ? 'Setlist published. Open it from the Setlist tab.'
                            : 'You build this setlist. The Setlist tab appears once it is published.'}
                      </p>
                      {view_.canBuild && (
                        <Button variant="dark" block className="mt-2.5" onClick={() => setBuilding(selectedPlan.id)}>
                          Build setlist
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}

              {readOnly && (
                <p className="text-[12.5px] text-t4 mt-3.5 leading-normal">
                  This service has happened. Its team and setlist are kept as a record and can&apos;t be changed.
                </p>
              )}
            </section>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          if (!selectedPlan) return;
          discardDraft(selectedPlan);
          setPendingYouTubeSongs([]);
          setBuilderKey((k) => k + 1);
          flash('Changes discarded', 'info');
        }}
        type="danger"
        title="Discard your changes?"
        message="The team, notes and setlist go back to the last saved version of this service. Nothing is sent to the team."
        confirmText="Discard changes"
        cancelText="Keep editing"
      />

      <RosterPublishModal isOpen={isPublishModalOpen} onClose={() => setIsPublishModalOpen(false)} onPublish={handlePublish} team={localTeam} isPublishing={isPublishing} />

      <EditServiceModal
        isOpen={isEditServiceOpen}
        onClose={() => setIsEditServiceOpen(false)}
        onSubmit={handleEditService}
        plan={selectedPlan}
        onRequestReschedule={() => {
          setIsEditServiceOpen(false);
          setIsRescheduleOpen(true);
        }}
      />

      <RescheduleServiceDialog
        isOpen={isRescheduleOpen}
        plan={selectedPlan}
        onClose={() => setIsRescheduleOpen(false)}
        onConfirm={async (newDateIso) => {
          if (!churchId || !selectedPlan) return;
          const result = await plansApi.reschedule(churchId, selectedPlan.id, newDateIso);
          const d = new Date(result.plan.date);
          if (result.wasCloned) {
            // Past plan: original kept as history, server returned a new plan.
            setAllPlans((prev) => [...prev, result.plan]);
            setSelectedPlanId(result.plan.id);
            flash('Service rescheduled. A new plan was created and the original was kept as history.');
          } else {
            // Future plan: in-place move; replace in list.
            setAllPlans((prev) => prev.map((p) => (p.id === result.plan.id ? result.plan : p)));
            flash('Service moved. Team confirmations were reset to Pending.');
          }
          setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        }}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText ?? (confirmDialog.type === 'danger' ? 'Delete' : 'Confirm')}
      />

    </div>
  );
};
